// src/pages/onboarding/quick-wins.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useOnboardingData } from "@/contexts/onboarding-data-context";
import { useOnboarding } from "@/contexts/onboarding-context";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { getOnboardingRedirectPath } from "@/lib/onboarding-navigation-utils";

import { PerformanceDropsAnalysis } from "@/components/onboarding/PerformanceDropsAnalysis";
import { CampaignSettingsAnalysis } from "@/components/onboarding/CampaignSettingsAnalysis";
import { AccountHealthAnalysis } from "@/components/onboarding/AccountHealthAnalysis";
import { ExecutiveSummary } from "@/components/onboarding/ExecutiveSummary";
import { ActionPlan } from "@/components/onboarding/ActionPlan";
import { NextStepsCard } from "@/components/onboarding/NextStepsCard";
import { DebugCard } from "@/components/onboarding/DebugCard";

const OnboardingQuickWinsPage = () => {
  const { data, loadingStatus, error, hasDataBeenFetched } =
    useOnboardingData();
  const { webAppUrl } = useOnboarding();
  const [minImpressions, setMinImpressions] = useState("1000");
  const [minConversions, setMinConversions] = useState("3");
  const [performanceDropsCount, setPerformanceDropsCount] = useState(0);
  const [geoTargetingIssuesCount, setGeoTargetingIssuesCount] = useState(0);
  const [contentNetworkIssuesCount, setContentNetworkIssuesCount] = useState(0);
  const [accountHealthIssuesCount, setAccountHealthIssuesCount] = useState(0);
  const [searchPartnersIssuesCount, setSearchPartnersIssuesCount] = useState(0);
  const [budgetConstrainedCount, setBudgetConstrainedCount] = useState(0);
  const [disapprovedAdsCount, setDisapprovedAdsCount] = useState(0);
  const [emptyAdGroupsCount, setEmptyAdGroupsCount] = useState(0);
  const [selectedTab, setSelectedTab] = useState<string>("overview");

  // Debug mode state
  const [debugOverride, setDebugOverride] = useState(null);
  const [debugScenario, setDebugScenario] = useState({
    name: "Real Data",
    performanceDrops: 0,
    geoTargeting: 0,
    contentNetwork: 0,
    accountHealth: 0,
    searchPartners: 0,
    budgetConstrained: 0,
    disapprovedAds: 0,
    emptyAdGroups: 0,
    description: "Using actual account data",
  });

  // Use debug values if override is enabled, otherwise use real data
  const effectivePerformanceDrops = debugOverride
    ? debugScenario.performanceDrops
    : performanceDropsCount;
  const effectiveGeoTargeting = debugOverride
    ? debugScenario.geoTargeting
    : geoTargetingIssuesCount;
  const effectiveContentNetwork = debugOverride
    ? debugScenario.contentNetwork
    : contentNetworkIssuesCount;
  const effectiveAccountHealth = debugOverride
    ? debugScenario.accountHealth
    : accountHealthIssuesCount;
  const effectiveSearchPartners = debugOverride
    ? debugScenario.searchPartners
    : searchPartnersIssuesCount;
  const effectiveBudgetConstrained = debugOverride
    ? debugScenario.budgetConstrained
    : budgetConstrainedCount;
  const effectiveDisapprovedAds = debugOverride
    ? debugScenario.disapprovedAds
    : disapprovedAdsCount;
  const effectiveEmptyAdGroups = debugOverride
    ? debugScenario.emptyAdGroups
    : emptyAdGroupsCount;

  const handleDebugScenarioChange = (scenario) => {
    setDebugScenario(scenario);
    setDebugOverride(true);
  };

  // All counts are now managed by their respective components via callbacks

  if (loadingStatus === "loading" && !data) {
    return (
      <main className="flex-1 flex items-center justify-center p-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Analyzing Your Account...</AlertTitle>
          <AlertDescription>
            {error
              ? `Error: ${error}`
              : "We're crunching the numbers to find your biggest opportunities."}
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  if (!hasDataBeenFetched || !data) {
    const redirect = getOnboardingRedirectPath(webAppUrl, !!data);

    return (
      <main className="flex-1 flex items-center justify-center p-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Data Available</AlertTitle>
          <AlertDescription className="space-y-4">
            <p>{redirect.message}</p>
            <Button asChild>
              <Link to={redirect.path}>Continue</Link>
            </Button>
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="relative">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">
            Your Quick Wins
          </h1>
          <p className="text-lg text-slate-600 mt-2">
            Immediate opportunities to improve your account performance
          </p>
        </div>

        {/* What's Next Button - Top Right */}
        <div className="absolute top-0 right-0">
          <NextStepsCard />
        </div>
      </div>

      {/* Executive Summary Section */}
      <ExecutiveSummary
        performanceDropsCount={effectivePerformanceDrops}
        geoTargetingIssuesCount={effectiveGeoTargeting}
        contentNetworkIssuesCount={effectiveContentNetwork}
        accountHealthIssuesCount={effectiveEmptyAdGroups}
        searchPartnersIssuesCount={effectiveSearchPartners}
        budgetConstrainedCount={effectiveBudgetConstrained}
        disapprovedAdsCount={effectiveDisapprovedAds}
        selectedTab={selectedTab}
        onTabSelect={setSelectedTab}
      />

      {/* Tab Content */}
      {selectedTab === "overview" && (
        <div className="space-y-6">
          {/* Performance Drops Analysis */}
          <PerformanceDropsAnalysis
            data={data?.on_performance}
            minImpressions={minImpressions}
            minConversions={minConversions}
            onMinImpressionsChange={setMinImpressions}
            onMinConversionsChange={setMinConversions}
            onCountChange={setPerformanceDropsCount}
          />

          {/* Campaign Settings Analysis */}
          <CampaignSettingsAnalysis
            data={data?.on_settings}
            onGeoTargetingCountChange={setGeoTargetingIssuesCount}
            onContentNetworkCountChange={setContentNetworkIssuesCount}
            onSearchPartnersCountChange={setSearchPartnersIssuesCount}
            onBudgetConstrainedCountChange={setBudgetConstrainedCount}
          />

          {/* Account Health Analysis */}
          <AccountHealthAnalysis
            disapprovedData={data?.on_disapproved}
            emptyAdGroupsData={data?.on_empty_ad_groups}
            onAccountHealthCountChange={setAccountHealthIssuesCount}
            onDisapprovedAdsCountChange={setDisapprovedAdsCount}
            onEmptyAdGroupsCountChange={setEmptyAdGroupsCount}
          />
        </div>
      )}

      {selectedTab === "performance-drops" && (
        <PerformanceDropsAnalysis
          data={data?.on_performance}
          minImpressions={minImpressions}
          minConversions={minConversions}
          onMinImpressionsChange={setMinImpressions}
          onMinConversionsChange={setMinConversions}
          onCountChange={setPerformanceDropsCount}
        />
      )}

      {(selectedTab === "geo-targeting" ||
        selectedTab === "search-partners" ||
        selectedTab === "display-network" ||
        selectedTab === "budget") && (
        <CampaignSettingsAnalysis
          data={data?.on_settings}
          onGeoTargetingCountChange={setGeoTargetingIssuesCount}
          onContentNetworkCountChange={setContentNetworkIssuesCount}
          onSearchPartnersCountChange={setSearchPartnersIssuesCount}
          onBudgetConstrainedCountChange={setBudgetConstrainedCount}
          focusedSection={
            selectedTab as
              | "geo-targeting"
              | "search-partners"
              | "display-network"
              | "budget"
          }
        />
      )}

      {(selectedTab === "disapproved-ads" ||
        selectedTab === "empty-ad-groups") && (
        <AccountHealthAnalysis
          disapprovedData={data?.on_disapproved}
          emptyAdGroupsData={data?.on_empty_ad_groups}
          onAccountHealthCountChange={setAccountHealthIssuesCount}
          onDisapprovedAdsCountChange={setDisapprovedAdsCount}
          onEmptyAdGroupsCountChange={setEmptyAdGroupsCount}
          focusedSection={selectedTab as "disapproved-ads" | "empty-ad-groups"}
        />
      )}

      {/* Action Plan - Bottom Card 
            <ActionPlan 
                performanceDropsCount={effectivePerformanceDrops}
                geoTargetingIssuesCount={effectiveGeoTargeting}
                contentNetworkIssuesCount={effectiveContentNetwork}
                accountHealthIssuesCount={effectiveEmptyAdGroups}
            />
            */}

      {/* Debug Card - Development Only */}
      {process.env.NODE_ENV === "development" && (
        <DebugCard
          onScenarioChange={handleDebugScenarioChange}
          currentScenario={debugScenario}
        />
      )}
    </div>
  );
};

export default OnboardingQuickWinsPage;
